import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@/routes/_student/problems/index';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useProblems, useProblemStats, useProblemCompanies } from '@/lib/hooks/useProblems';
import {
  Search, ChevronDown, X,
  Circle, CheckCircle2, ExternalLink,
  Code2, Flame, Target, Sparkles,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';

const CARD = 'rounded-[20px] border border-[#ececec] bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]';

const TOPICS = [
  { label: 'Array', slug: 'array' },
  { label: 'String', slug: 'string' },
  { label: 'Hash Table', slug: 'hash-table' },
  { label: 'Dynamic Programming', slug: 'dynamic-programming' },
  { label: 'Math', slug: 'math' },
  { label: 'Sorting', slug: 'sorting' },
  { label: 'Greedy', slug: 'greedy' },
  { label: 'Binary Search', slug: 'binary-search' },
  { label: 'DFS', slug: 'depth-first-search' },
  { label: 'BFS', slug: 'breadth-first-search' },
  { label: 'Bit Manipulation', slug: 'bit-manipulation' },
  { label: 'Matrix', slug: 'matrix' },
  { label: 'Two Pointers', slug: 'two-pointers' },
  { label: 'Tree', slug: 'tree' },
  { label: 'Binary Tree', slug: 'binary-tree' },
  { label: 'Heap', slug: 'heap-priority-queue' },
  { label: 'Stack', slug: 'stack' },
  { label: 'Graph', slug: 'graph' },
  { label: 'Sliding Window', slug: 'sliding-window' },
  { label: 'Prefix Sum', slug: 'prefix-sum' },
  { label: 'Backtracking', slug: 'backtracking' },
  { label: 'Linked List', slug: 'linked-list' },
  { label: 'Trie', slug: 'trie' },
  { label: 'Union Find', slug: 'union-find' },
  { label: 'Database', slug: 'database' },
  { label: 'Design', slug: 'design' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy',   label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard',   label: 'Hard' },
];

const STATUS_OPTIONS = [
  { value: 'solved',    label: 'Solved' },
  { value: 'attempted', label: 'Attempted' },
  { value: 'unsolved',  label: 'Unsolved' },
];

const PAGE_SIZES = [10, 25, 50];

const TAB_OPTIONS: { value: 'all' | 'dsa' | 'sql' | 'genai'; label: string }[] = [
  { value: 'all', label: 'All Problems' },
  { value: 'dsa', label: 'DSA' },
  { value: 'sql', label: 'SQL' },
  { value: 'genai', label: 'Generative AI' },
];

const DIFF_STYLE: Record<string, { bg: string; color: string }> = {
  easy:   { bg: '#dcfce7', color: '#16a34a' },
  medium: { bg: '#fef3c7', color: '#d97706' },
  hard:   { bg: '#fee2e2', color: '#dc2626' },
};

// ── Dropdown with inline clear ─────────────────────────────────────────────
interface DropdownOption { value: string; label: string }

function Dropdown({
  label, value, options, onSelect, onClear, scrollable = false,
}: {
  label: string;
  value?: string;
  options: DropdownOption[];
  onSelect: (v: string) => void;
  onClear: () => void;
  scrollable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-[10px] border pl-3 text-[13px] font-medium transition-colors',
          selected
            ? 'border-[#8b5cf6] bg-[#f5f3ff] text-[#7c3aed] pr-1.5'
            : 'border-[#e5e7eb] bg-white text-[#374151] hover:border-[#9ca3af] pr-3',
        )}
      >
        {selected ? selected.label : label}
        {selected ? (
          // Inline clear button — no need to reopen dropdown
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onClear(); } }}
            className="ml-0.5 flex size-6 items-center justify-center rounded-[6px] text-[#7c3aed] transition-colors hover:bg-[#ede9fe]"
          >
            <X className="size-3.5" />
          </span>
        ) : (
          <ChevronDown className="size-3.5 text-[#9ca3af]" />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-30 min-w-[160px] rounded-[14px] border border-[#ececec] bg-white p-1 shadow-[0px_8px_24px_rgba(0,0,0,0.1)]">
          <div className={scrollable ? 'max-h-[220px] overflow-y-auto' : undefined}>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onSelect(o.value); setOpen(false); }}
                className={cn(
                  'flex w-full items-center rounded-[10px] px-3 py-2 text-left text-[13px] font-medium transition-colors hover:bg-[#f9fafb]',
                  value === o.value ? 'bg-[#f5f3ff] text-[#7c3aed]' : 'text-[#374151]',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Segmented tab control (All / DSA / SQL) ────────────────────────────────
function SegmentedTabs<T extends string>({
  value, options, onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Problem category"
      className="inline-flex items-center gap-1 rounded-[12px] border border-[#ececec] bg-[#f9fafb] p-1"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[9px] px-4 py-1.5 text-[13px] font-semibold transition-colors',
            value === o.value
              ? 'bg-white text-[#7c3aed] shadow-[0px_1px_2px_rgba(0,0,0,0.08)]'
              : 'text-[#6b7280] hover:text-[#374151]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Stat item ──────────────────────────────────────────────────────────────
function StatItem({
  iconEl, value, name, subtitle,
}: {
  iconEl: ReactNode; value: string; name: string; subtitle: string;
}) {
  return (
    <div className="flex items-center gap-4 px-8 py-2">
      {iconEl}
      <div>
        <p className="text-[26px] font-bold leading-none text-[#111827]">{value}</p>
        <p className="mt-1 text-[14px] font-semibold text-[#111827]">{name}</p>
        <p className="mt-0.5 text-[12px] text-[#9ca3af]">{subtitle}</p>
      </div>
    </div>
  );
}

// ── Page numbers ───────────────────────────────────────────────────────────
function buildPages(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, '...', total];
  if (current >= total - 2) return [1, '...', total - 2, total - 1, total];
  return [1, '...', current, '...', total];
}

// ── Main component ─────────────────────────────────────────────────────────
export function ProblemListPage() {
  const { tab = 'all', tag, company, difficulty, status, q = '', page = 1, limit: urlLimit } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const limit = urlLimit ?? 10;

  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== q) setSearch({ q: searchInput, page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSearchInput(q); }, [q]);

  function setSearch(patch: {
    tab?: 'all' | 'dsa' | 'sql' | 'genai'; tag?: string; company?: string; difficulty?: string; status?: string;
    q?: string; page?: number; limit?: number;
  }) {
    navigate({
      search: (prev) => {
        const next = { ...prev, ...patch };
        if (!next.tab || next.tab === 'all') delete next.tab;
        if (!next.tag) delete next.tag;
        if (!next.company) delete next.company;
        if (!next.difficulty) delete next.difficulty;
        if (!next.status) delete next.status;
        if (!next.q) delete next.q;
        if (!next.page || next.page === 1) delete next.page;
        // keep limit in URL only when non-default
        if (!next.limit || next.limit === 10) delete next.limit;
        return next;
      },
      replace: true,
    });
  }

  const { data, isLoading } = useProblems({ tab, difficulty, tag, company, page, q, status, limit });
  const { data: statsData } = useProblemStats();
  const { data: companies = [] } = useProblemCompanies();

  const solvedSet = new Set(statsData?.solved_problem_ids ?? []);

  const problems   = data?.problems ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from       = total === 0 ? 0 : (page - 1) * limit + 1;
  const to         = Math.min(page * limit, total);
  const pages      = buildPages(page, totalPages);

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-6 px-4 py-6 sm:px-10 sm:py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-x-1.5 text-[13px] font-medium text-[#9ca3af]" aria-label="Breadcrumb">
        <Link to="/dashboard" className="transition-colors hover:text-[#ff5a1f]">Dashboard</Link>
        <span aria-hidden>/</span>
        <span className="font-semibold text-[#111827]">Problems</span>
      </nav>

      {/* Hero */}
      <header className="flex items-center gap-5">
        <span
          className="flex size-16 shrink-0 items-center justify-center rounded-[20px] sm:size-[72px]"
          style={{ background: 'linear-gradient(135deg, #ddd6fe 0%, #ede9fe 100%)' }}
        >
          <Code2 className="size-8 text-[#7c3aed]" />
        </span>
        <div>
          <h1 className="text-[34px] font-bold leading-[1.05] tracking-tight text-[#111827] sm:text-[44px]">
            Problems
          </h1>
          <p className="mt-1 text-[15px] text-[#6b7280]">
            Solve coding challenges and improve your skills.
          </p>
        </div>
      </header>

      {/* Stats */}
      <div className={`${CARD} grid grid-cols-3 divide-x divide-[#ececec] py-6`}>
        <StatItem
          iconEl={
            <span className="flex size-12 shrink-0 items-center justify-center rounded-[14px]" style={{ background: '#ede9fe' }}>
              <CheckCircle2 className="size-6 text-[#8b5cf6]" />
            </span>
          }
          value={statsData ? String(statsData.problems_solved) : '—'}
          name="Problems Solved"
          subtitle="Total"
        />
        <StatItem
          iconEl={
            <span className="flex size-12 shrink-0 items-center justify-center rounded-[14px]" style={{ background: '#fff5f0' }}>
              <Flame className="size-6 text-[#ff5a1f]" />
            </span>
          }
          value={statsData ? String(statsData.current_streak) : '—'}
          name="Current Streak"
          subtitle="Days"
        />
        <StatItem
          iconEl={
            <span className="flex size-12 shrink-0 items-center justify-center rounded-[14px]" style={{ background: '#dcfce7' }}>
              <Target className="size-6 text-[#22c55e]" />
            </span>
          }
          value={statsData ? `${Math.round(statsData.accuracy_this_week)}%` : '—'}
          name="Accuracy"
          subtitle="This Week"
        />
      </div>

      {/* Category tabs */}
      <SegmentedTabs
        value={tab}
        options={TAB_OPTIONS}
        onChange={(v) => setSearch({ tab: v, page: 1 })}
      />

      {/* Filter bar */}
      <div className={`${CARD} flex items-center gap-3 p-4`}>
        <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-[10px] border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2">
          <Search className="size-4 shrink-0 text-[#9ca3af]" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search problems..."
            className="flex-1 bg-transparent text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af]"
          />
          {searchInput && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch({ q: '', page: 1 }); }}>
              <X className="size-3.5 text-[#9ca3af] hover:text-[#374151]" />
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3">
          <Dropdown
            label="Difficulty"
            value={difficulty}
            options={DIFFICULTY_OPTIONS}
            onSelect={(v) => setSearch({ difficulty: v, page: 1 })}
            onClear={() => setSearch({ difficulty: '', page: 1 })}
          />
          <Dropdown
            label="Topic"
            value={tag}
            options={TOPICS.map((t) => ({ value: t.slug, label: t.label }))}
            onSelect={(v) => setSearch({ tag: v, page: 1 })}
            onClear={() => setSearch({ tag: '', page: 1 })}
            scrollable
          />
          <Dropdown
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onSelect={(v) => setSearch({ status: v, page: 1 })}
            onClear={() => setSearch({ status: '', page: 1 })}
          />
          <Dropdown
            label="Company"
            value={company}
            options={companies.map((c) => ({ value: c.company, label: `${c.company} (${c.problem_count})` }))}
            onSelect={(v) => setSearch({ company: v, page: 1 })}
            onClear={() => setSearch({ company: '', page: 1 })}
            scrollable
          />
        </div>
      </div>

      {/* Table card */}
      <div className={`${CARD} overflow-hidden`}>
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-14" />
            <col />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-24" />
            <col className="w-24" />
            <col className="w-32" />
          </colgroup>
          <thead className="border-b border-[#f3f4f6]">
            <tr>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Status</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Problem</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Difficulty</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Topic</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Solved</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Accuracy</th>
              <th className="px-4 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#f3f4f6]">
            {isLoading ? (
              Array.from({ length: limit }).map((_, i) => (
                <tr key={i} className="h-[60px]">
                  {[5, 45, 10, 10, 10, 8, 8].map((w, j) => (
                    <td key={j} className="px-5 py-4">
                      <div className="h-4 animate-pulse rounded-full bg-[#f3f4f6]" style={{ width: `${w}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : problems.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center text-[14px] text-[#9ca3af]">
                  No problems found.
                </td>
              </tr>
            ) : (
              problems.map((p) => {
                const diff    = DIFF_STYLE[p.difficulty] ?? DIFF_STYLE.easy;
                const topic   = p.tags?.[0] ? p.tags[0].replace(/-/g, ' ') : '—';
                const isSolved = solvedSet.has(p.id);

                return (
                  <tr
                    key={p.id}
                    className="h-[60px] cursor-pointer transition-colors hover:bg-[#fafafa]"
                    onClick={() => navigate({ to: '/problems/$slug', params: { slug: p.slug } })}
                  >
                    <td className="px-5 py-4">
                      {isSolved
                        ? <CheckCircle2 className="size-5 text-[#22c55e]" />
                        : <Circle className="size-5 text-[#d1d5db]" />
                      }
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <p className={cn('truncate text-[15px] font-semibold', isSolved ? 'text-[#22c55e]' : 'text-[#111827]')}>
                          {p.title}
                        </p>
                        {p.execution_mode === 'prompt' && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#f5f3ff] px-2 py-0.5 text-[11px] font-semibold text-[#7c3aed]">
                            <Sparkles className="size-3" />
                            Prompt
                          </span>
                        )}
                      </div>
                      {p.description && (
                        <p className="mt-0.5 truncate text-[13px] text-[#9ca3af]">{p.description}</p>
                      )}
                      {p.company_tags && p.company_tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {p.company_tags.slice(0, 3).map((c: string) => (
                            <span
                              key={c}
                              className="inline-flex items-center rounded-full bg-[#f5f3ff] px-2 py-0.5 text-[11px] font-medium text-[#7c3aed]"
                            >
                              {c}
                            </span>
                          ))}
                          {p.company_tags.length > 3 && (
                            <span className="inline-flex items-center rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[11px] font-medium text-[#9ca3af]">
                              +{p.company_tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex rounded-[6px] px-2.5 py-0.5 text-[12px] font-semibold capitalize"
                        style={{ background: diff.bg, color: diff.color }}
                      >
                        {p.difficulty}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[14px] capitalize text-[#374151] whitespace-nowrap overflow-hidden text-ellipsis">
                      {topic}
                    </td>
                    <td className="px-5 py-4 text-[14px] text-[#9ca3af]">—</td>
                    <td className="px-5 py-4 text-[14px] text-[#9ca3af]">—</td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => navigate({ to: '/problems/$slug', params: { slug: p.slug } })}
                        className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-semibold text-[#374151] transition-colors hover:border-[#9ca3af] hover:bg-[#f9fafb]"
                      >
                        Open <ExternalLink className="size-3" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className={`${CARD} flex flex-wrap items-center justify-between gap-3 px-5 py-3.5`}>
          <p className="text-[13px] text-[#9ca3af]">
            Showing {from}–{to} of {total.toLocaleString()} problems
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSearch({ page: page - 1 })}
              disabled={page === 1}
              className="flex size-8 items-center justify-center rounded-[8px] border border-[#e5e7eb] text-[#6b7280] transition-colors hover:bg-[#f9fafb] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="size-4" />
            </button>

            {pages.map((p, i) =>
              p === '...' ? (
                <span key={`el-${i}`} className="flex size-8 items-center justify-center text-[13px] text-[#9ca3af]">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSearch({ page: p as number })}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full text-[13px] font-semibold transition-colors',
                    page === p
                      ? 'bg-[#ff5a1f] text-white'
                      : 'border border-[#e5e7eb] text-[#374151] hover:bg-[#f9fafb]',
                  )}
                >
                  {p}
                </button>
              ),
            )}

            <button
              type="button"
              onClick={() => setSearch({ page: page + 1 })}
              disabled={page === totalPages}
              className="flex size-8 items-center justify-center rounded-[8px] border border-[#e5e7eb] text-[#6b7280] transition-colors hover:bg-[#f9fafb] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          {/* Per-page selector */}
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[#9ca3af]">Per page:</span>
            <div className="flex items-center gap-1">
              {PAGE_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setSearch({ limit: size, page: 1 })}
                  className={cn(
                    'flex h-7 min-w-[32px] items-center justify-center rounded-[6px] px-2 text-[12px] font-semibold transition-colors',
                    limit === size
                      ? 'bg-[#f5f3ff] text-[#7c3aed]'
                      : 'text-[#6b7280] hover:bg-[#f9fafb]',
                  )}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
